import { useEffect, useRef, useState } from 'react';
import { echeanceAtteinte, lireEcheance, poserEcheance } from './cadence';

/**
 * Une sollicitation qui revient à intervalle, et jamais plus souvent.
 *
 * La première visite pose l'échéance sans rien afficher. C'est volontaire : un compte
 * qu'on vient d'ouvrir, ou un parent invité qui découvre le dossier, n'a pas à être
 * accueilli par une proposition d'abonnement. La première sollicitation attend donc un
 * intervalle entier.
 *
 * `honorer` est appelé à la **fermeture**, pas à l'ouverture : si la fenêtre ne s'est
 * jamais affichée — écran quitté aussitôt, rendu interrompu —, l'échéance n'est pas
 * consommée et la sollicitation reste due.
 */
export function useSollicitation(
  cle: string,
  intervalle: () => number,
): { due: boolean; honorer: () => void } {
  const [due, setDue] = useState(false);
  const tirer = useRef(intervalle);
  tirer.current = intervalle;

  useEffect(() => {
    const maintenant = Date.now();
    const prochaine = lireEcheance(cle);
    if (prochaine === null) {
      poserEcheance(cle, maintenant + tirer.current());
      return;
    }
    if (echeanceAtteinte(prochaine, maintenant)) setDue(true);
    // `intervalle` est une fonction de tirage tenue hors des dépendances *via* la
    // référence ci-dessus : la rappeler à chaque rendu redéciderait la date de retour en
    // permanence, et l'échéance ne tomberait jamais.
  }, [cle]);

  return {
    due,
    honorer: () => {
      poserEcheance(cle, Date.now() + tirer.current());
      setDue(false);
    },
  };
}
